import classNames from 'classnames';
import React, { memo, useRef, useState } from 'react';
import moment, { Moment } from 'moment';
import { Button, Popover } from 'antd';
import { ReactComponent as DownArrowSvg } from '@assets/icons/arrow-down.svg';
import { ReactComponent as CrossSvg } from '@assets/icons/cross.svg';
import InlineRangePicker from '@components/DatePickerComponent/InlineRangePicker';

type DatesFilterProps = {
  onChange: (value: [string, string] | null) => void;
  date: string[] | null;
  placeholder?: string;
  overlayClassName?: string;
  timeOptions?: (
    | 'today'
    | 'yesterday'
    | 'tomorrow'
    | 'prevWeek'
    | 'prevMonth'
    | 'nextWeek'
    | 'nextMonth'
  )[];
};

function formattedRangeTime(
  range: [Moment, Moment] | null
): [string, string] | null {
  if (!range || range?.length < 2) return null;
  const localStart = range[0].clone();
  const localEnd = range[1].clone();

  const newDate: [string, string] = [
    moment
      .utc({
        year: localStart.year(),
        month: localStart.month(),
        day: localStart.date(),
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0
      })
      .toISOString(),
    moment
      .utc({
        year: localEnd.year(),
        month: localEnd.month(),
        day: localEnd.date(),
        hour: 23,
        minute: 59,
        second: 59,
        millisecond: 999
      })
      .toISOString()
  ];
  return newDate;
}

export default memo(function DatesFilter({
  onChange,
  date,
  overlayClassName,
  placeholder = 'Due Date',
  timeOptions = ['today', 'tomorrow']
}: DatesFilterProps) {
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState<[Moment, Moment] | null>(() =>
    date?.length === 2
      ? [moment.parseZone(date[0]), moment.parseZone(date[1])]
      : null
  );
  const prevDate = useRef(date);
  if (date !== prevDate.current) {
    prevDate.current = date;
    setRange(
      date?.length === 2
        ? [moment.parseZone(date[0]), moment.parseZone(date[1])]
        : null
    );
  }

  const selectRange = (from: Moment, to: Moment) => {
    setRange([from, to]);
    const newDueDate = formattedRangeTime([from, to]);
    onChange(newDueDate);
    prevDate.current = newDueDate;
    setOpen(false);
  };

  const reset = () => {
    if (date !== null) onChange(null);
    if (range !== null) {
      prevDate.current = null;
      setRange(null);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
        if (!open) reset();
      }}
      trigger="click"
      placement="right"
      overlayClassName={classNames(
        'popover-container date-picker-popover-container',
        overlayClassName
      )}
      content={
        <>
          {!!timeOptions.length && (
            <div className="range-picker-header-container">
              <span>Choose a time</span>
              {timeOptions.includes('today') && (
                <span
                  onClick={() =>
                    selectRange(moment().startOf('day'), moment().endOf('day'))
                  }
                >
                  Today
                </span>
              )}
              {timeOptions.includes('yesterday') && (
                <span
                  onClick={() =>
                    selectRange(
                      moment().subtract(1, 'day').startOf('day'),
                      moment().subtract(1, 'day').endOf('day')
                    )
                  }
                >
                  Yesterday
                </span>
              )}
              {timeOptions.includes('tomorrow') && (
                <span
                  onClick={() =>
                    selectRange(
                      moment().add(1, 'day').startOf('day'),
                      moment().add(1, 'day').endOf('day')
                    )
                  }
                >
                  Tomorrow
                </span>
              )}
              {timeOptions.includes('prevMonth') && (
                <span
                  onClick={() =>
                    selectRange(
                      moment().subtract(1, 'week').startOf('week'),
                      moment().subtract(1, 'week').endOf('week')
                    )
                  }
                >
                  Previous week
                </span>
              )}
              {timeOptions.includes('prevMonth') && (
                <span
                  onClick={() =>
                    selectRange(
                      moment().subtract(1, 'month').startOf('month'),
                      moment().subtract(1, 'month').endOf('month')
                    )
                  }
                >
                  Previous month
                </span>
              )}
              {timeOptions.includes('nextWeek') && (
                <span
                  onClick={() =>
                    selectRange(
                      moment().add(1, 'week').startOf('week'),
                      moment().add(1, 'week').endOf('week')
                    )
                  }
                >
                  Next week
                </span>
              )}
              {timeOptions.includes('nextMonth') && (
                <span
                  onClick={() =>
                    selectRange(
                      moment().add(1, 'month').startOf('month'),
                      moment().add(1, 'month').endOf('month')
                    )
                  }
                >
                  Next month
                </span>
              )}
            </div>
          )}
          <InlineRangePicker value={range} onChange={setRange} />
          <div className="range-picker-footer-container">
            <Button
              type="text"
              onClick={() => {
                setRange(range || null);
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              disabled={!range}
              onClick={() => {
                if (!range) return;
                selectRange(range[0].startOf('day'), range[1].endOf('day'));
              }}
            >
              Save
            </Button>
          </div>
        </>
      }
    >
      <div
        className="filter-item"
        id="due-date-range-filter"
        style={{ width: '100%', maxWidth: 154 }}
      >
        {date && date[1] && (
          <Button
            type="text"
            className="filter-item__clear"
            onClick={(e) => {
              e.stopPropagation();
              reset();
            }}
          >
            <CrossSvg />
          </Button>
        )}
        <span
          style={{ whiteSpace: 'nowrap' }}
          className={classNames(
            'filter-item__name',
            date && date[1] && 'filter-item__active-name'
          )}
        >
          {date && date[1]
            ? moment.parseZone(date[1]).format('MMM DD, YYYY')
            : placeholder}
        </span>
        <div className="filter-item-icon">
          <DownArrowSvg />
        </div>
      </div>
    </Popover>
  );
});
